import React, { useRef, useMemo, useEffect } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import { Mesh, TextureLoader, Vector3, MeshStandardMaterial, DoubleSide, Color, Euler } from 'three'
import { collisionSystem } from '../utils/CollisionSystem'
import { mmlCollisionSystem } from '../utils/MMLCollisionSystem'
import { animatedParcelTracker } from '../utils/AnimatedParcelTracker'

// Custom hook to create grid texture
const useGridTexture = () => {
  return useMemo(() => {
    const canvas = document.createElement('canvas')
    canvas.width = 200
    canvas.height = 200
    const ctx = canvas.getContext('2d')!

    // Set background
    const gradient = ctx.createLinearGradient(0, 0, 200, 200)
    gradient.addColorStop(0, '#00FF00') // Verde
    gradient.addColorStop(1, '#FF00FF') // Roxo
    ctx.fillStyle = gradient
    ctx.fillRect(0, 0, 200, 200)

    // Draw grid
    ctx.strokeStyle = '#000000' // Black grid lines
    ctx.lineWidth = 2

    // Create nodes and connections
    const nodes: Array<{x: number, y: number}> = []
    const cols = 8
    const rows = 10

    // Generate nodes with slight randomization
    for (let x = 0; x <= cols; x++) {
      for (let y = 0; y <= rows; y++) {
        const px = (x / cols) * 200 + (Math.random() * 20 - 10)
        const py = (y / rows) * 200 + (Math.random() * 20 - 10)
        nodes.push({ x: px, y: py })

        // Draw node
        ctx.beginPath()
        ctx.arc(px, py, 2, 0, Math.PI * 2)
        ctx.fillStyle = '#FFFFFF'
        ctx.fill()
      }
    }

    // Connect nearby nodes
    for (let i = 0; i < nodes.length; i++) {
      for (let j = i + 1; j < nodes.length; j++) {
        const dx = nodes[i].x - nodes[j].x
        const dy = nodes[i].y - nodes[j].y
        const distance = Math.sqrt(dx * dx + dy * dy)

        if (distance < 50) {
          ctx.beginPath()
          ctx.moveTo(nodes[i].x, nodes[i].y)
          ctx.lineTo(nodes[j].x, nodes[j].y)
          ctx.stroke()
        }
      }
    }

    return new TextureLoader().load(canvas.toDataURL())
  }, [])
}

interface SpaceshipProps {
  position?: [number, number, number]
  rotation?: [number, number, number]
  onPositionChange?: (position: { x: number, y: number, z: number }, rotation: { x: number, y: number, z: number }) => void
  isRemotePlayer?: boolean // Indica se é uma nave de outro jogador
  ref?: React.Ref<Mesh> // Permite passar uma ref externa
  otherShips?: { position: Vector3 }[] // Lista de outras naves para verificar colisões
  playerId?: string // ID do jogador para identificar a nave
}

export const Spaceship = React.forwardRef<Mesh, SpaceshipProps>(({
  position = [0, 0, 0],
  rotation = [0, 0, 0],
  onPositionChange,
  isRemotePlayer = false,
  otherShips = [],
  playerId = 'local'
}, ref) => {
  // Use a ref passada ou crie uma nova
  const internalRef = useRef<Mesh>(null)
  const shipRef = (ref as React.RefObject<Mesh>) || internalRef
  const gridTexture = useGridTexture()
  const { camera } = useThree()

  // Movement state
  const moveState = useRef({
    forward: false,
    backward: false,
    left: false,
    right: false,
    up: false,
    down: false,
    speed: 0.5, // Velocidade aumentada em 5x (era 0.1)
    isShiftPressed: false,
    cameraAngleX: 0,
    cameraAngleY: Math.PI / 4,
    cameraDistance: 30,
    targetCameraDistance: 30, // Alvo para smooth zoom
    zoomVelocity: 0, // Velocidade atual do zoom para inércia
    lastMouseX: 0,
    lastMouseY: 0,
    isDragging: false,
    minZoom: 5, // Reduzido ainda mais para permitir zoom extremamente próximo
    maxZoom: 120, // Aumentado significativamente para permitir zoom muito distante
    zoomSmoothingFactor: 0.12, // Fator de suavização para o zoom (0-1) - reduzido para maior suavidade
    zoomInertia: 0.9 // Fator de inércia para o zoom (0-1) - aumentado para maior inércia
  })

  // Log de inicialização
  useEffect(() => {
    console.log('Inicializando componente Spaceship');
  }, []);

  // Setup keyboard and mouse controls - apenas para jogador local
  useEffect(() => {
    // Skip para jogadores remotos
    if (isRemotePlayer) return;

    // Função para resetar todos os estados de movimento
    const resetAllMovement = () => {
      moveState.current.forward = false;
      moveState.current.backward = false;
      moveState.current.left = false;
      moveState.current.right = false;
      moveState.current.up = false;
      moveState.current.down = false;
      moveState.current.isShiftPressed = false;
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignorar eventos repetidos de keydown para evitar problemas
      if (e.repeat) return;

      switch(e.key.toLowerCase()) {
        case 'w': moveState.current.forward = true; break;
        case 's': moveState.current.backward = true; break;
        case 'a': moveState.current.left = true; break;
        case 'd': moveState.current.right = true; break;
        case ' ': moveState.current.up = true; break; // Tecla espaço
        case 'control': moveState.current.down = true; break; // Tecla control
        case 'shift': moveState.current.isShiftPressed = true; break; // Tecla shift
        case 'r': // Reset position
          if (shipRef.current) {
            shipRef.current.position.set(...position);
            shipRef.current.rotation.set(0, 0, 0);
          }
          break;
      }
    }

    const handleKeyUp = (e: KeyboardEvent) => {
      switch(e.key.toLowerCase()) {
        case 'w': moveState.current.forward = false; break;
        case 's': moveState.current.backward = false; break;
        case 'a': moveState.current.left = false; break;
        case 'd': moveState.current.right = false; break;
        case ' ': moveState.current.up = false; break; // Tecla espaço
        case 'control': moveState.current.down = false; break; // Tecla control
        case 'shift': moveState.current.isShiftPressed = false; break; // Tecla shift
      }
    }

    // Resetar movimento quando a janela perde o foco
    const handleBlur = () => {
      resetAllMovement();
    };

    const handleMouseDown = (e: MouseEvent) => {
      moveState.current.isDragging = true
      moveState.current.lastMouseX = e.clientX
      moveState.current.lastMouseY = e.clientY
    }

    const handleMouseUp = () => {
      moveState.current.isDragging = false
    }

    const handleMouseMove = (e: MouseEvent) => {
      if (moveState.current.isDragging) {
        const deltaX = e.clientX - moveState.current.lastMouseX
        const deltaY = e.clientY - moveState.current.lastMouseY

        // Invertemos o sinal do deltaX para corrigir o movimento invertido
        // (quando move para esquerda, a câmera gira para direita e vice-versa)
        moveState.current.cameraAngleX -= deltaX * 0.01

        // Removemos as limitações do ângulo vertical para permitir rotação completa de 360 graus
        moveState.current.cameraAngleY += deltaY * 0.01

        // Normalizamos o ângulo para mantê-lo entre 0 e 2*PI
        moveState.current.cameraAngleY = moveState.current.cameraAngleY % (Math.PI * 2)
        if (moveState.current.cameraAngleY < 0) moveState.current.cameraAngleY += Math.PI * 2

        moveState.current.lastMouseX = e.clientX
        moveState.current.lastMouseY = e.clientY
      }
    }

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault()

      // Variable Zoom: Ajusta a velocidade do zoom com base na distância atual
      // Zoom mais rápido quando distante, mais lento quando próximo
      const currentDistance = moveState.current.cameraDistance
      const distanceRange = moveState.current.maxZoom - moveState.current.minZoom
      const distanceRatio = (currentDistance - moveState.current.minZoom) / distanceRange

      // Função logarítmica aprimorada para velocidade de zoom variável
      // Zoom extremamente lento quando muito próximo (5% da velocidade normal)
      // Zoom normal em distâncias médias
      // Zoom muito rápido quando muito distante (até 300% da velocidade normal)
      const baseZoomSpeed = 1.8 // Aumentado para zoom mais rápido em geral

      // Função de velocidade não-linear mais sofisticada
      // Usa uma curva sigmoide modificada para transições mais suaves entre extremos
      let variableZoomSpeed;

      if (distanceRatio < 0.1) {
        // Zoom extremamente próximo - velocidade muito reduzida (5-15%)
        variableZoomSpeed = baseZoomSpeed * (0.05 + distanceRatio);
      } else if (distanceRatio > 0.8) {
        // Zoom muito distante - velocidade aumentada (200-300%)
        variableZoomSpeed = baseZoomSpeed * (2.0 + (distanceRatio - 0.8) * 5);
      } else {
        // Zona média - velocidade variando suavemente
        variableZoomSpeed = baseZoomSpeed * (0.3 + 2.0 * Math.pow(distanceRatio, 1.2));
      }

      // Direção do zoom (positivo = afastar, negativo = aproximar)
      const zoomDirection = Math.sign(e.deltaY)

      // Calcular o delta de zoom com velocidade variável
      const delta = e.deltaY * 0.01 * variableZoomSpeed

      // Atualizar a velocidade do zoom para inércia
      moveState.current.zoomVelocity += delta * 0.8

      // Atualizar o alvo da distância da câmera com limites
      moveState.current.targetCameraDistance = Math.max(
        moveState.current.minZoom,
        Math.min(
          moveState.current.maxZoom,
          moveState.current.targetCameraDistance + delta
        )
      )

      // Log para debug
      console.log(`Zoom: distance=${currentDistance.toFixed(2)}, ratio=${distanceRatio.toFixed(2)}, speed=${variableZoomSpeed.toFixed(2)}, delta=${delta.toFixed(2)}`)
    }

    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('keyup', handleKeyUp)
    window.addEventListener('mousedown', handleMouseDown)
    window.addEventListener('mouseup', handleMouseUp)
    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('wheel', handleWheel, { passive: false }) // Adiciona o handler de zoom
    window.addEventListener('blur', handleBlur) // Adiciona handler para quando a janela perde o foco
    window.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        resetAllMovement();
      }
    })

    // Resetar movimento ao montar o componente para garantir estado inicial limpo
    resetAllMovement();

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('keyup', handleKeyUp)
      window.removeEventListener('mousedown', handleMouseDown)
      window.removeEventListener('mouseup', handleMouseUp)
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('wheel', handleWheel) // Remove o handler de zoom
      window.removeEventListener('blur', handleBlur)
      window.removeEventListener('visibilitychange', () => {})

      // Garantir que os estados de movimento sejam resetados ao desmontar
      resetAllMovement();
    }
  }, [])

  // Materials
  const bodyMaterial = useMemo(() => {
    const material = new MeshStandardMaterial({
      map: gridTexture,
      metalness: 0.7,
      roughness: 0.2,
      side: DoubleSide
    })
    return material
  }, [gridTexture])

  const domeMaterial = useMemo(() =>
    new MeshStandardMaterial({
      transparent: true,
      opacity: 0.4,  // Mais transparente
      metalness: 0.1, // Menos metálico para parecer mais vidro
      roughness: 0.05, // Mais liso para refletir melhor
      color: new Color('#FFA500'),
      emissive: new Color('#FFA500'),
      emissiveIntensity: 0.3, // Mais brilho
      envMapIntensity: 1.5 // Aumenta reflexões
    }), []
  )

  const legMaterial = useMemo(() =>
    new MeshStandardMaterial({
      color: new Color('#8B4513'),
      metalness: 0.6,
      roughness: 0.3
    }), []
  )

  const propulsorMaterial = useMemo(() =>
    new MeshStandardMaterial({
      color: new Color('#000000'),
      metalness: 0.9,
      roughness: 0.1
    }), []
  )

  // Animation and movement
  useFrame(() => {
    if (shipRef.current) {
      const ship = shipRef.current

      // Skip para jogadores remotos (eles são controlados pelo OtherPlayerSpaceship)
      if (isRemotePlayer) return;

      // Set initial position only on first render or teleport
      if (!ship.userData.initialized ||
          (position[0] !== ship.userData.lastPosition?.[0] ||
           position[1] !== ship.userData.lastPosition?.[1] ||
           position[2] !== ship.userData.lastPosition?.[2])) {
        ship.position.set(...position);
        // Garantir que a nave sempre inicie reta (sem inclinação)
        ship.rotation.x = 0;
        ship.rotation.z = 0;
        ship.userData.lastPosition = [...position];
        ship.userData.initialized = true;
        // Report initial position
        onPositionChange?.({
          x: ship.position.x,
          y: ship.position.y,
          z: ship.position.z
        }, {
          x: 0, // Sempre reportar rotação X como zero
          y: ship.rotation.y,
          z: 0  // Sempre reportar rotação Z como zero
        });
      }

      // Calculate camera position e atualiza apenas para jogador local
      if (!isRemotePlayer) {
        // Smooth Zoom Aprimorado: Implementação mais sofisticada com easing adaptativo
        // Aplicar inércia à velocidade do zoom com decaimento exponencial
        moveState.current.zoomVelocity *= moveState.current.zoomInertia;

        // Calcular a diferença entre a distância atual e a distância alvo
        const distanceDiff = moveState.current.targetCameraDistance - moveState.current.cameraDistance;
        const absDiff = Math.abs(distanceDiff);

        // Easing adaptativo - mais suave para pequenas mudanças, mais rápido para grandes mudanças
        // Isso cria uma sensação mais natural de movimento
        let adaptiveSmoothingFactor;

        if (absDiff < 1.0) {
          // Para diferenças muito pequenas, usar um fator de suavização muito baixo
          // Isso evita oscilações no final do movimento
          adaptiveSmoothingFactor = moveState.current.zoomSmoothingFactor * 0.5;
        } else if (absDiff > 20.0) {
          // Para diferenças muito grandes, usar um fator de suavização maior
          // Isso permite que a câmera se mova mais rapidamente para grandes distâncias
          adaptiveSmoothingFactor = moveState.current.zoomSmoothingFactor * 1.5;
        } else {
          // Para diferenças médias, usar o fator de suavização padrão
          adaptiveSmoothingFactor = moveState.current.zoomSmoothingFactor;
        }

        // Aplicar easing cúbico para criar uma curva de aceleração/desaceleração mais natural
        // Combinar com a velocidade atual para criar um efeito de inércia
        const smoothingStep = distanceDiff * adaptiveSmoothingFactor + moveState.current.zoomVelocity;

        // Atualizar a distância da câmera
        moveState.current.cameraDistance += smoothingStep;

        // Garantir que a distância esteja dentro dos limites
        moveState.current.cameraDistance = Math.max(
          moveState.current.minZoom,
          Math.min(moveState.current.maxZoom, moveState.current.cameraDistance)
        );

        // Calcular a posição da câmera com base na distância atualizada
        const cameraX = ship.position.x + Math.sin(moveState.current.cameraAngleX) * Math.cos(moveState.current.cameraAngleY) * moveState.current.cameraDistance;
        const cameraY = ship.position.y + Math.sin(moveState.current.cameraAngleY) * moveState.current.cameraDistance;
        const cameraZ = ship.position.z + Math.cos(moveState.current.cameraAngleX) * Math.cos(moveState.current.cameraAngleY) * moveState.current.cameraDistance;

        // Update camera
        camera.position.set(cameraX, cameraY, cameraZ);
        camera.lookAt(ship.position);
      }

      // Calculate current speed based on shift key state
      // Multiplicador de velocidade com Shift aumentado para 5x (era 2x)
      const currentSpeed = moveState.current.isShiftPressed ? moveState.current.speed * 5 : moveState.current.speed;

      // Manual movement - apenas para jogador local
      if (!isRemotePlayer) {
        const moveVector = new Vector3()
        if (moveState.current.forward) moveVector.z -= currentSpeed * 1.5 // Velocidade extra para frente (tecla W)
        if (moveState.current.backward) moveVector.z += currentSpeed
        if (moveState.current.left) moveVector.x -= currentSpeed
        if (moveState.current.right) moveVector.x += currentSpeed
        if (moveState.current.up) moveVector.y += currentSpeed
        if (moveState.current.down) moveVector.y -= currentSpeed

        // Apply movement relative to camera direction
        if (moveVector.length() > 0) {
          const cameraDirection = new Vector3(0, 0, -1)
          cameraDirection.applyEuler(new Euler(0, moveState.current.cameraAngleX, 0))
          // Aplicamos a rotação apenas nos eixos X e Z, preservando o movimento vertical
          const horizontalMove = new Vector3(moveVector.x, 0, moveVector.z)
          horizontalMove.applyEuler(new Euler(0, moveState.current.cameraAngleX, 0))
          moveVector.x = horizontalMove.x
          moveVector.z = horizontalMove.z

          // Salvar a posição atual antes de aplicar o movimento
          const previousPosition = ship.position.clone();

          // Sistema de desvio físico suave quando encontrar barreiras
          // Verificar colisão ANTES de aplicar o movimento (detecção preventiva)
          const potentialPosition = previousPosition.clone().add(moveVector);
          const wouldCollide = mmlCollisionSystem.checkCollision(potentialPosition);

          // Verificar se está próximo de uma barreira para aplicar um desvio suave preventivo
          const closestBarrierDistance = mmlCollisionSystem.getDistanceToClosestBarrier(previousPosition);
          const proximityThreshold = 1.5; // Distância para começar a aplicar o desvio suave

          // Se estiver próximo de uma barreira mas ainda não colidindo, aplicar um desvio suave
          if (!wouldCollide && closestBarrierDistance < proximityThreshold) {
            // Calcular um fator de desvio baseado na proximidade (mais próximo = desvio mais forte)
            const deviationFactor = 1.0 - (closestBarrierDistance / proximityThreshold);

            // Obter a direção para se afastar da barreira mais próxima
            const closestBarrier = mmlCollisionSystem.findClosestBarrier(previousPosition);
            if (closestBarrier) {
              const barrierCenter = new Vector3(
                closestBarrier.position.x + closestBarrier.size.width / 2,
                closestBarrier.position.y + closestBarrier.size.height / 2,
                closestBarrier.position.z + closestBarrier.size.depth / 2
              );

              // Calcular direção de afastamento
              const awayDirection = new Vector3().subVectors(previousPosition, barrierCenter).normalize();

              // Aplicar um pequeno desvio na direção de afastamento
              const deviationStrength = 0.1 * deviationFactor;
              moveVector.add(awayDirection.multiplyScalar(deviationStrength));
            }
          }

          // Armazenar o vetor de movimento original para cálculos de reflexão
          const originalMoveVector = moveVector.clone();

          // Aplicar efeito físico de desvio se houver colisão potencial
          if (wouldCollide) {
            // Encontrar a barreira mais próxima para calcular o desvio
            const closestBarrier = mmlCollisionSystem.findClosestBarrier(potentialPosition);

            if (closestBarrier) {
              // Obter a altura real da parcela
              let effectiveHeight = closestBarrier.size.height;
              if (closestBarrier.isAnimated) {
                const currentHeight = animatedParcelTracker.getCurrentParcelHeight();
                if (currentHeight !== null) {
                  effectiveHeight = currentHeight - closestBarrier.position.y;
                }
              }

              // Calcular os limites exatos da parcela
              const minX = closestBarrier.position.x;
              const maxX = closestBarrier.position.x + closestBarrier.size.width;
              const minY = closestBarrier.position.y;
              const maxY = closestBarrier.position.y + effectiveHeight;
              const minZ = closestBarrier.position.z;
              const maxZ = closestBarrier.position.z + closestBarrier.size.depth;

              // Determinar qual face da barreira está mais próxima
              const faces = [
                { normal: new Vector3(-1, 0, 0), dist: Math.abs(potentialPosition.x - minX), name: 'left' },
                { normal: new Vector3(1, 0, 0), dist: Math.abs(potentialPosition.x - maxX), name: 'right' },
                { normal: new Vector3(0, -1, 0), dist: Math.abs(potentialPosition.y - maxY), name: 'top' },
                { normal: new Vector3(0, 1, 0), dist: Math.abs(potentialPosition.y - minY), name: 'bottom' },
                { normal: new Vector3(0, 0, -1), dist: Math.abs(potentialPosition.z - minZ), name: 'front' },
                { normal: new Vector3(0, 0, 1), dist: Math.abs(potentialPosition.z - maxZ), name: 'back' },
              ];

              // Encontrar a face mais próxima
              faces.sort((a, b) => a.dist - b.dist);
              const closestFace = faces[0];

              // Calcular o vetor de reflexão baseado na normal da face
              // Fórmula de reflexão: R = V - 2(V·N)N onde V é o vetor de movimento, N é a normal da face
              const normal = closestFace.normal;
              const dot = originalMoveVector.dot(normal);

              // Aplicar um fator de conservação de energia (0.6 = 60% da energia é conservada)
              // Reduzido para criar um desvio mais suave
              const energyConservation = 0.6;

              // Calcular o vetor de reflexão
              const reflectionVector = new Vector3();
              reflectionVector.copy(originalMoveVector);
              reflectionVector.sub(normal.clone().multiplyScalar(2 * dot));
              reflectionVector.multiplyScalar(energyConservation);

              // Adicionar um componente de deslizamento ao longo da superfície
              // Isso cria um efeito mais suave de deslizar ao longo das paredes
              const slideVector = new Vector3();

              // Ajustar o vetor de deslizamento com base na face atingida
              if (closestFace.name === 'top' || closestFace.name === 'bottom') {
                // Se colidir com o topo ou fundo, deslizar horizontalmente
                slideVector.set(originalMoveVector.x, 0, originalMoveVector.z);
              } else if (closestFace.name === 'left' || closestFace.name === 'right') {
                // Se colidir com as laterais, deslizar verticalmente e no eixo Z
                slideVector.set(0, originalMoveVector.y * 0.5, originalMoveVector.z);
              } else {
                // Se colidir com frente ou trás, deslizar verticalmente e no eixo X
                slideVector.set(originalMoveVector.x, originalMoveVector.y * 0.5, 0);
              }

              // Normalizar e ajustar a força do deslizamento
              // Aumentado para criar um deslizamento mais suave e natural
              if (slideVector.length() > 0) {
                slideVector.normalize().multiplyScalar(0.25);
              }

              // Combinar reflexão e deslizamento para um efeito físico mais natural
              // 40% reflexão, 60% deslizamento para um desvio mais suave
              moveVector.copy(reflectionVector.multiplyScalar(0.4).add(slideVector.multiplyScalar(0.6)));

              // Adicionar um pequeno impulso para cima para evitar ficar preso
              if (closestFace.name !== 'top') {
                moveVector.y += 0.05;
              }

              // Aplicar um efeito de amortecimento para movimentos muito rápidos
              // Ajustado para criar um desvio mais suave e natural
              if (moveVector.length() > 0.4) {
                moveVector.normalize().multiplyScalar(0.4);
              }
            }
          }

          // Verificar especificamente se o movimento vertical para baixo causaria uma colisão com o topo de uma parcel
          // Esta é uma verificação crucial para impedir que a nave passe por dentro das parcels
          if (moveVector.y < 0) { // Se estiver descendo
            // Criar um raio vertical para baixo a partir da posição atual
            const rayStart = previousPosition.clone();
            const rayEnd = rayStart.clone();
            rayEnd.y -= 2.0; // Verificar até 2 unidades abaixo

            // Verificar todas as parcels para ver se há alguma diretamente abaixo
            const barriers = mmlCollisionSystem.getBarriers();
            let willHitParcelTop = false;
            let topParcelY = -Infinity;

            for (const barrier of barriers) {
              // Verificar se a nave está dentro dos limites horizontais da parcel
              const isWithinHorizontalBounds =
                rayStart.x >= barrier.position.x && rayStart.x <= barrier.position.x + barrier.size.width &&
                rayStart.z >= barrier.position.z && rayStart.z <= barrier.position.z + barrier.size.depth;

              if (isWithinHorizontalBounds) {
                // Obter a altura real da parcela
                let effectiveHeight = barrier.size.height;
                if (barrier.isAnimated) {
                  const currentHeight = animatedParcelTracker.getCurrentParcelHeight();
                  if (currentHeight !== null) {
                    effectiveHeight = currentHeight - barrier.position.y;
                  }
                }

                // Ajustar a barreira de colisão para ficar mais alta que o topo visual real
                // Adicionar um offset para garantir que a colisão ocorra acima do topo visual
                const visualTopOffset = 1.0; // Offset para elevar a barreira de colisão
                const parcelVisualTop = barrier.position.y + visualTopOffset; // Topo visual elevado

                // Verificar se a nave está acima do topo visual da parcel e se o raio interceptaria esse topo
                if (rayStart.y > parcelVisualTop && rayEnd.y < parcelVisualTop && parcelVisualTop > topParcelY) {
                  willHitParcelTop = true;
                  topParcelY = parcelVisualTop;
                  console.log(`Detectada colisão com o TOPO VISUAL da parcel ${barrier.id} na altura ${parcelVisualTop}`);
                }
              }
            }

            // Se o raio interceptar o topo visual da parcel, ajustar o movimento para parar exatamente nesse topo
            if (willHitParcelTop) {
              // Calcular a distância até o topo visual da parcel
              const distanceToTop = rayStart.y - topParcelY;

              // Usar uma margem ainda maior para garantir que a nave pare bem acima do topo visual real
              // Isso evita completamente que a nave entre na parcel
              const safetyMargin = 1.0;

              // Limitar o movimento vertical para não ultrapassar o topo visual da parcel
              if (Math.abs(moveVector.y) > distanceToTop - safetyMargin) {
                moveVector.y = -(distanceToTop - safetyMargin);
                console.log(`Movimento vertical ajustado para parar ${safetyMargin} unidades acima do topo visual`);
              }
            }
          }

          // Aplicar o movimento (potencialmente modificado para desviar)
          ship.position.add(moveVector);

          // Verificar se a nave está sobre uma parcela animada
          if (animatedParcelTracker.shouldRiseWithParcel()) {
            // Obter a altura atual da parcela
            const parcelHeight = animatedParcelTracker.getCurrentParcelHeight();

            // Verificar se a nave está acima ou tocando a parcela (com uma margem maior)
            if (parcelHeight !== null && ship.position.y < (parcelHeight + 1.0)) {
              // Obter a diferença de altura desde a última atualização
              const heightDifference = animatedParcelTracker.getHeightDifference();

              // Aplicar o efeito físico de empurrar a nave para cima
              // Limitamos a diferença de altura para evitar saltos bruscos
              if (heightDifference > 0) {
                // Definir a posição Y da nave para estar sempre acima da parcela com margem maior
                ship.position.y = Math.max(ship.position.y, parcelHeight + 1.0);
              }
            }
          }

          // Verificar se há colisão com parcelas ou com o "vazio" (fora do mapa)
          let hasCollision = mmlCollisionSystem.checkCollision(ship.position);

          // Se ainda houver colisão (caso o desvio não tenha funcionado), aplicar um sistema de escape físico
          if (hasCollision) {
            // Voltar à posição anterior
            ship.position.copy(previousPosition);

            // Criar um efeito de "ricochete" físico
            // Primeiro encontrar a barreira mais próxima
            const closestBarrier = mmlCollisionSystem.findClosestBarrier(ship.position);

            if (closestBarrier) {
              // Calcular um vetor de escape baseado na posição relativa à barreira
              const barrierCenter = new Vector3(
                closestBarrier.position.x + closestBarrier.size.width / 2,
                closestBarrier.position.y + closestBarrier.size.height / 2,
                closestBarrier.position.z + closestBarrier.size.depth / 2
              );

              // Vetor da barreira para a nave (direção de escape)
              const escapeDirection = new Vector3().subVectors(ship.position, barrierCenter).normalize();

              // Adicionar um componente vertical para garantir que a nave suba um pouco
              escapeDirection.y = Math.max(0.3, escapeDirection.y);

              // Normalizar novamente e aplicar uma força de escape
              escapeDirection.normalize().multiplyScalar(0.3);

              // Aplicar o movimento de escape com um efeito de mola
              ship.position.add(escapeDirection);

              // Adicionar um pequeno impulso aleatório para evitar ficar preso em ciclos
              const randomImpulse = new Vector3(
                (Math.random() - 0.5) * 0.1,
                Math.random() * 0.1,
                (Math.random() - 0.5) * 0.1
              );
              ship.position.add(randomImpulse);

              // Verificar se o movimento de escape resolveu a colisão
              if (mmlCollisionSystem.checkCollision(ship.position)) {
                // Se ainda houver colisão, tentar um movimento mais radical
                ship.position.copy(previousPosition);
                ship.position.y += 0.8; // Mover para cima com uma força menor, mais natural
              }
            } else {
              // Fallback se não encontrar barreira
              const escapeVector = new Vector3(0, 0.3, -0.3);
              ship.position.add(escapeVector);
            }
          }

          // Verificar colisão com outras naves
          if (otherShips.length > 0) {
            // Converter as posições das outras naves para Vector3
            const otherShipPositions = otherShips.map(ship => ship.position);

            // Verificar colisão com outras naves (usando um raio de colisão menor para que as naves precisem estar mais próximas)
            const shipCollision = collisionSystem.checkSpaceshipCollisions(ship.position, otherShipPositions, 0.8); // Aumentado para 2x

            // Se houver colisão com outra nave
            if (shipCollision.hasCollision) {
              // Obter a posição da nave com a qual colidiu
              const collidingShipPosition = otherShipPositions[shipCollision.collidingShipIndex];

              // Obter a posição corrigida após colisão com a nave
              const shipCorrectedPosition = collisionSystem.correctSpaceshipCollision(
                ship.position,
                collidingShipPosition,
                previousPosition,
                0.8 // Raio de colisão aumentado para 2x
              );

              // Aplicar a posição corrigida
              ship.position.copy(shipCorrectedPosition);

              hasCollision = true;

              // Log para debug
              console.log(`Nave ${playerId} colidiu com outra nave`);
            }
          }

          // Manter a nave sempre reta, sem efeitos de inclinação
          if (hasCollision) {
            // Resetar rotações X e Z para manter a nave sempre reta
            ship.rotation.x = 0;
            ship.rotation.z = 0;

            // Manter apenas a rotação Y para que a nave gire horizontalmente na direção do movimento
            // Não adicionar efeitos aleatórios de rotação
          }

          // Report position change with rotation (sempre mantendo a nave reta)
          onPositionChange?.({
            x: ship.position.x,
            y: ship.position.y,
            z: ship.position.z
          }, {
            x: 0, // Sempre reportar rotação X como zero
            y: ship.rotation.y,
            z: 0  // Sempre reportar rotação Z como zero
          });

          // Update ship rotation to face movement direction
          const targetRotation = Math.atan2(moveVector.x, -moveVector.z)
          const currentRotation = ship.rotation.y
          ship.rotation.y = currentRotation + (targetRotation - currentRotation) * 0.1
        }
      }
    }
  })

  return (
    <mesh ref={shipRef} position={position} rotation={rotation}>
      {/* Main body (oval with grid pattern) */}
      <mesh material={bodyMaterial} scale={[1.0, 0.34, 1.0]}>
        <sphereGeometry args={[1, 32, 32]} />
      </mesh>

      {/* Transparent orange dome */}
      <mesh material={domeMaterial} position={[0, 0.26, 0]} scale={[0.40, 0.46, 0.40]}>
        <sphereGeometry args={[1, 32, 32]} />
      </mesh>

      {/* Support legs - apenas 2 pernas */}
      <group position={[0, -0.26, 0]}>
        {/* Left leg - posição ajustada */}
        <mesh material={legMaterial} position={[-0.4, 0, 0.1]}>
          <boxGeometry args={[0.06, 0.4, 0.06]} />
        </mesh>
        {/* Right leg - posição ajustada */}
        <mesh material={legMaterial} position={[0.4, 0, 0.1]}>
          <boxGeometry args={[0.06, 0.4, 0.06]} />
        </mesh>
      </group>

      {/* Side propulsors (closer together) */}
      <group position={[0, -0.14, -0.46]}>
        {/* Left propulsor */}
        <mesh material={propulsorMaterial} position={[-0.2, 0, 0]} rotation={[0, 0, Math.PI / 2]}>
          <cylinderGeometry args={[0.1, 0.1, 0.26, 16]} />
        </mesh>
        {/* Right propulsor */}
        <mesh material={propulsorMaterial} position={[0.2, 0, 0]} rotation={[0, 0, Math.PI / 2]}>
          <cylinderGeometry args={[0.1, 0.1, 0.26, 16]} />
        </mesh>
      </group>
    </mesh>
  )
})